/**
 * Hook that resolves conversation IDs to conversation names.
 * Fetches conversation details via RTK Query and caches results
 * to avoid re-fetching on subsequent renders.
 */

import { useLazyGetAgentConversationQuery } from '@saaga/shared/store/api.store'
import { useEffect, useRef, useState } from 'react'

export type ConversationNameMap = Map<string, string>

const filterUnfetchedIds = (ids: string[], fetchedIds: Set<string>): string[] =>
  ids.filter((id) => id && !fetchedIds.has(id))

const buildStableKey = (ids: string[]): string => [...ids].sort().join(',')

export const useConversationNamesLookup = (conversationIds: string[]): ConversationNameMap => {
  const [trigger] = useLazyGetAgentConversationQuery()
  const [nameMap, setNameMap] = useState<ConversationNameMap>(new Map())
  const fetchedIdsRef = useRef<Set<string>>(new Set())
  const cachedMapRef = useRef<ConversationNameMap>(new Map())

  const stableKey = buildStableKey(conversationIds)

  useEffect(() => {
    const idsToFetch = filterUnfetchedIds(conversationIds, fetchedIdsRef.current)
    if (idsToFetch.length === 0) return

    const fetchConversationNames = async (): Promise<void> => {
      const results = await Promise.allSettled(idsToFetch.map((id) => trigger(id).unwrap()))

      let hasNewEntries = false
      for (const [index, result] of results.entries()) {
        const id = idsToFetch[index]
        fetchedIdsRef.current.add(id)
        if (result.status === 'fulfilled' && result.value.name) {
          cachedMapRef.current.set(id, result.value.name)
          hasNewEntries = true
        }
      }

      if (hasNewEntries) {
        setNameMap(new Map(cachedMapRef.current))
      }
    }

    void fetchConversationNames()
  }, [stableKey, trigger])

  return nameMap
}
